import { GOOD_ROAD } from '@/types/global'
import { RoadMapItem } from '../roadmap/datamodel/RoadMapItem'
import { GoodRoad, TypeBead } from '../roadmap/datamodel/types'

/** 庄 / 龙 */
const SIDE_A = TypeBead.RED

/** 闲 / 虎 */
const SIDE_B = TypeBead.BLUE

interface Column {
    type: number
    count: number
}

function toColumns(items: RoadMapItem[]): Column[] {
    return items
        .filter((col) => col.type === SIDE_A || col.type === SIDE_B)
        .map((col) => ({ type: col.type, count: col.size }))
}

function makeGoodRoad(id: number): GoodRoad {
    const entry = GOOD_ROAD[id]
    return {
        id,
        type: entry?.type ?? 0,
        color: entry?.color ?? -1,
        map: [],
        exemptions: []
    }
}

function countAlternatingSingleColumnsFromEnd(columns: Column[]): number {
    let count = 0
    let prevType = -1

    for (let i = columns.length - 1; i >= 0; i--) {
        const col = columns[i]
        if (col.count !== 1) break
        if (prevType !== -1 && col.type === prevType) break
        count++
        prevType = col.type
    }

    return count
}

/** 1. 长庄 / 长龙 — last column SIDE_A with count ≥ 4 */
function isChangA(columns: Column[]): boolean {
    const last = columns[columns.length - 1]
    return last.type === SIDE_A && last.count >= 4
}

/** 2. 长闲 / 长虎 — last column SIDE_B with count ≥ 4 */
function isChangB(columns: Column[]): boolean {
    const last = columns[columns.length - 1]
    return last.type === SIDE_B && last.count >= 4
}

/** 3. 大路单跳 — ≥ 4 trailing single columns that alternate */
function isDaLuDanTiao(columns: Column[]): boolean {
    return countAlternatingSingleColumnsFromEnd(columns) >= 4
}

/** 4. 一庄两闲 — last 4 columns: 庄1-闲2-庄1-闲2 */
function isYiALiangB(columns: Column[]): boolean {
    if (columns.length < 4) return false

    const last4 = columns.slice(-4)
    return (
        last4[0].type === SIDE_A &&
        last4[0].count === 1 &&
        last4[1].type === SIDE_B &&
        last4[1].count === 2 &&
        last4[2].type === SIDE_A &&
        last4[2].count === 1 &&
        last4[3].type === SIDE_B &&
        last4[3].count === 2
    )
}

/** 5. 一闲两庄 — last 4 columns: 闲1-庄2-闲1-庄2 */
function isYiBLiangA(columns: Column[]): boolean {
    if (columns.length < 4) return false

    const last4 = columns.slice(-4)
    return (
        last4[0].type === SIDE_B &&
        last4[0].count === 1 &&
        last4[1].type === SIDE_A &&
        last4[1].count === 2 &&
        last4[2].type === SIDE_B &&
        last4[2].count === 1 &&
        last4[3].type === SIDE_A &&
        last4[3].count === 2
    )
}

/** 6. 长路转单跳 — long column (≥4) then ≥3 alternating single columns */
function isChangLuZhuanDanTiao(columns: Column[]): boolean {
    const singleCount = countAlternatingSingleColumnsFromEnd(columns)
    if (singleCount < 3) return false

    const longRoadIndex = columns.length - singleCount - 1
    if (longRoadIndex < 0) return false

    const longRoadCol = columns[longRoadIndex]
    const firstSingleCol = columns[longRoadIndex + 1]

    if (longRoadCol.count < 4) return false
    if (firstSingleCol.type === longRoadCol.type) return false

    return true
}

/** 7. 逢庄跳 — last 6 columns: every 庄 column has exactly 1 bead */
function isFengATiao(columns: Column[]): boolean {
    if (columns.length < 6) return false

    const last6 = columns.slice(-6)
    if (!last6.some((col) => col.type === SIDE_A)) return false

    return last6.every((col) => col.type !== SIDE_A || col.count === 1)
}

/** 8. 逢闲跳 — last 6 columns: every 闲 column has exactly 1 bead */
function isFengBTiao(columns: Column[]): boolean {
    if (columns.length < 6) return false

    const last6 = columns.slice(-6)
    if (!last6.some((col) => col.type === SIDE_B)) return false

    return last6.every((col) => col.type !== SIDE_B || col.count === 1)
}

/** 9. 逢庄连 — last 6 columns: 庄≥2 闲≥1 alternating × 3 */
function isFengALian(columns: Column[]): boolean {
    if (columns.length < 6) return false

    const last6 = columns.slice(-6)
    const types = [SIDE_A, SIDE_B, SIDE_A, SIDE_B, SIDE_A, SIDE_B]
    const mins = [2, 1, 2, 1, 2, 1]

    return last6.every(
        (col, index) => col.type === types[index] && col.count >= mins[index]
    )
}

/** 10. 逢闲连 — last 6 columns: 闲≥2 庄≥1 alternating × 3 */
function isFengBLian(columns: Column[]): boolean {
    if (columns.length < 6) return false

    const last6 = columns.slice(-6)
    const types = [SIDE_B, SIDE_A, SIDE_B, SIDE_A, SIDE_B, SIDE_A]
    const mins = [2, 1, 2, 1, 2, 1]

    return last6.every(
        (col, index) => col.type === types[index] && col.count >= mins[index]
    )
}

/**
 * Detect at most one good road per table (priority order 1→10),
 * matching GoodRoadDetector.kt.
 */
export function detectGoodRoads(columns: RoadMapItem[]): GoodRoad[] {
    const cols = toColumns(columns)
    if (!cols.length) return []

    if (isChangA(cols)) return [makeGoodRoad(1)]
    if (isChangB(cols)) return [makeGoodRoad(2)]
    if (isDaLuDanTiao(cols)) return [makeGoodRoad(3)]
    if (isYiALiangB(cols)) return [makeGoodRoad(4)]
    if (isYiBLiangA(cols)) return [makeGoodRoad(5)]
    if (isChangLuZhuanDanTiao(cols)) return [makeGoodRoad(6)]
    if (isFengATiao(cols)) return [makeGoodRoad(7)]
    if (isFengBTiao(cols)) return [makeGoodRoad(8)]
    if (isFengALian(cols)) return [makeGoodRoad(9)]
    if (isFengBLian(cols)) return [makeGoodRoad(10)]

    return []
}
